import React, { useEffect, useState } from "react";
import { apiCall } from "../services/firebase";
import { pluginService } from "../services/PluginService";
import { Box, Paper, Typography, List, ListItem, ListItemText, Divider, CircularProgress, Alert, Button } from "@mui/material";

export const DebugEventsPage: React.FC = () => {
  const [events, setEvents] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isTriggering, setIsTriggering] = useState(false);
  const [triggerResult, setTriggerResult] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [resetResult, setResetResult] = useState<string | null>(null);

  const fetchEvents = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log("[DebugEventsPage.fetchEvents] Starting fetch...");
      const response = await apiCall("/getEventsForUser");
      console.log("[DebugEventsPage.fetchEvents] Received response:", response);
      const events = response.events || [];
      console.log(`[DebugEventsPage.fetchEvents] Got ${events.length} events`);

      // Log all events' timestamps to debug
      events.forEach((event: any, index: number) => {
        console.log(`[DebugEventsPage.fetchEvents] Event ${index + 1}/${events.length}:`, {
          id: event.id,
          type: event.type,
          timestamp: event.timestamp,
          timestampType: typeof event.timestamp,
          timestampValue: event.timestamp,
        });
      });

      // Helper to get timestamp as number for sorting - prefer data.timestamp over event.timestamp
      const getTimestampValue = (event: any): number => {
        // Use data.timestamp if available, otherwise use event.timestamp
        const timestamp = event.data?.timestamp || event.timestamp;
        if (!timestamp) return 0;

        try {
          let date: Date;
          if (typeof timestamp === "string") {
            date = new Date(timestamp);
          } else if (timestamp?.toDate && typeof timestamp.toDate === "function") {
            date = timestamp.toDate();
          } else if (timestamp instanceof Date) {
            date = timestamp;
          } else {
            date = new Date(timestamp);
          }

          return isNaN(date.getTime()) ? 0 : date.getTime();
        } catch {
          return 0;
        }
      };

      // Sort events by timestamp (latest first) - ensure proper sorting in UI
      const sortedEvents = events.sort((a: any, b: any) => {
        return getTimestampValue(b) - getTimestampValue(a); // Descending (latest first)
      });

      setEvents(sortedEvents);
    } catch (err) {
      setError("Failed to fetch events.");
    } finally {
      setIsLoading(false);
    }
  };

  const triggerPluginPoll = async () => {
    setIsTriggering(true);
    setTriggerResult(null);
    try {
      await pluginService.pollForCurrentUser();
      setTriggerResult("Plugin polling for current user triggered successfully!");
      // Refresh events after triggering
      setTimeout(() => fetchEvents(), 1000);
    } catch (err) {
      setTriggerResult("Failed to trigger plugin polling for current user.");
    } finally {
      setIsTriggering(false);
    }
  };

  const resetUserData = async () => {
    if (!window.confirm("Are you sure you want to reset all your data? This action cannot be undone and will delete all your agents, threads, messages, and events.")) {
      return;
    }

    setIsResetting(true);
    setResetResult(null);
    try {
      const response = await apiCall("/resetUserData", {
        method: "POST",
      });
      setResetResult(`Reset completed successfully! Deleted ${response.deletedCount} documents.`);
      // Clear events after reset
      setEvents([]);
    } catch (err) {
      setResetResult("Failed to reset user data.");
    } finally {
      setIsResetting(false);
    }
  };

  useEffect(() => {
    // Initialize the service if not already done
    pluginService.init();

    fetchEvents();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Debug: All User Events (EventBus)
      </Typography>
      <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
        <Button variant="outlined" onClick={fetchEvents} disabled={isLoading}>
          Refresh Events
        </Button>
        <Button variant="contained" color="primary" onClick={triggerPluginPoll} disabled={isTriggering}>
          {isTriggering ? "Triggering..." : "Trigger Plugin Poll"}
        </Button>
        <Button variant="contained" color="error" onClick={resetUserData} disabled={isResetting}>
          {isResetting ? "Resetting..." : "Reset All Data"}
        </Button>
      </Box>
      {isLoading && <CircularProgress sx={{ display: "block", my: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {triggerResult && (
        <Alert severity={triggerResult.includes("successfully") ? "success" : "error"} sx={{ mb: 2 }}>
          {triggerResult}
        </Alert>
      )}
      {resetResult && (
        <Alert severity={resetResult.includes("successfully") ? "success" : "error"} sx={{ mb: 2 }}>
          {resetResult}
        </Alert>
      )}

      {!isLoading && events.length === 0 && !error && <Typography>No events found.</Typography>}
      <Paper sx={{ mt: 2, p: 2 }}>
        <List>
          {events.map((event, eventIndex) => {
            // Prefer data.timestamp over event.timestamp (ISO string from backend)
            const eventTimestamp = event.data?.timestamp || event.timestamp;

            console.log(`[DebugEventsPage.render] Event ${eventIndex + 1}:`, {
              id: event.id,
              type: event.type,
              eventTimestamp: event.timestamp,
              dataTimestamp: event.data?.timestamp,
              usingTimestamp: eventTimestamp,
              timestampType: typeof eventTimestamp,
              rawEvent: event,
            });

            // Format timestamp for display - backend sends ISO string
            let timestampDisplay = "No timestamp";
            if (eventTimestamp) {
              try {
                // Backend sends ISO string, parse it directly
                const date = typeof eventTimestamp === "string" ? new Date(eventTimestamp) : new Date(eventTimestamp);
                if (!isNaN(date.getTime())) {
                  timestampDisplay = date.toLocaleString();
                  console.log(`[DebugEventsPage.render] Event ${eventIndex + 1} timestamp parsed: ${eventTimestamp} -> ${date.toISOString()} -> ${timestampDisplay}`);
                } else {
                  timestampDisplay = `Invalid: ${eventTimestamp}`;
                  console.warn(`[DebugEventsPage.render] Event ${eventIndex + 1} invalid timestamp: ${eventTimestamp}`);
                }
              } catch (error) {
                timestampDisplay = `Error: ${eventTimestamp}`;
                console.error(`[DebugEventsPage.render] Event ${eventIndex + 1} timestamp parse error:`, error, eventTimestamp);
              }
            } else {
              console.warn(`[DebugEventsPage.render] Event ${eventIndex + 1} has no timestamp field (checked both event.timestamp and event.data.timestamp)`);
            }

            return (
              <React.Fragment key={event.id}>
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="subtitle1">{event.type || "(No Type)"}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                          {timestampDisplay}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <>
                        {event.data && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Data: {JSON.stringify(event.data, null, 2)}
                          </Typography>
                        )}
                        {event.team && (
                          <Typography variant="body2" color="text.secondary">
                            Team: {event.team}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            );
          })}
        </List>
      </Paper>
    </Box>
  );
};
